import { AppState } from '../types';

// --- API CONFIGURATION ---
// Changed to relative path so it works in subdirectories (e.g. /test/api/...)
const API_BASE = 'api';

// UPDATE: Switch to SQL-based endpoint
const SAVE_ENDPOINT = `${API_BASE}/save_to_sql.php`;
const LOAD_ENDPOINT = `${API_BASE}/get_state_from_sql.php`;

export const saveRemoteContent = async (state: AppState): Promise<{success: boolean, error?: string}> => {
    try {
        const token = sessionStorage.getItem('adminToken') || state.config.adminToken;
        const response = await fetch(SAVE_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: token, state: state })
        });
        
        if (!response.ok) {
            const text = await response.text();
            throw new Error(`Server error: ${response.status} - ${text}`);
        }
        
        return await response.json();
    } catch (e) {
        console.error("Save error:", e);
        return { success: false, error: String(e) };
    }
};

export const loadRemoteContent = async (): Promise<AppState | null> => {
    try {
        // In CMS mode, index.php usually injects INITIAL_STATE into window.
        // However, we can also fetch it if needed.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((window as any).INITIAL_STATE) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (window as any).INITIAL_STATE as AppState;
        }

        // UPDATE: Switch to SQL-based endpoint
        const response = await fetch(LOAD_ENDPOINT);
        if (!response.ok) return null;
        
        const data = await response.json();
        
        // If SQL DB is empty/fresh, API might return null.
        // In that case, we return null so AppContext falls back to DEFAULT_STATE.
        if (!data) return null;

        // If data is valid, return it
        if (data && data.pages) return data as AppState;
        
        return null;
    } catch (e) {
        console.warn("Could not load remote content:", e);
        return null;
    }
};

export const uploadFile = async (file: File): Promise<{success: boolean, url?: string, error?: string}> => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        const token = sessionStorage.getItem('adminToken');
        if(token) formData.append('token', token);

        const response = await fetch(`${API_BASE}/upload.php`, {
            method: 'POST',
            body: formData
        });

        if (!response.ok) throw new Error('Upload failed');
        return await response.json();
    } catch (e) {
        return { success: false, error: String(e) };
    }
};

// Snapshot logic remains for local browser history/undo
const STORAGE_KEY_SNAPSHOTS = 'envicon_snapshots';

export const saveSnapshot = (name: string, state: AppState) => {
  const snapshots = getSnapshots();
  const newSnapshot = { id: `snap-${Date.now()}`, name, timestamp: Date.now(), state: { ...state, isEditMode: false } };
  snapshots.unshift(newSnapshot);
  localStorage.setItem(STORAGE_KEY_SNAPSHOTS, JSON.stringify(snapshots.slice(0, 10))); // Limit to 10
};

export const getSnapshots = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY_SNAPSHOTS) || '[]'); } catch (e) { return []; }
};

export const deleteSnapshot = (id: string) => {
  const snapshots = getSnapshots().filter(s => s.id !== id);
  localStorage.setItem(STORAGE_KEY_SNAPSHOTS, JSON.stringify(snapshots));
};